<template>
  <div ref="rootEl" class="rounded-xl bg-container-bg py-5 px-4">
    <div class="flex justify-between items-center">
      <div class="min-w-0 flex-1">
        <slot name="header" />
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <slot name="ctas" />
        <button
          v-if="is_collapsable"
          type="button"
          class="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-cta-tertiary-outline-icon hover:bg-container-bg hover:text-strongest active:scale-95 transition-colors"
          :aria-expanded="!is_collapsed_effective"
          @click="toggle"
        >
          <Icon
            name="ChevronDown"
            class="h-5.5 w-5.5 transition-transform duration-400"
            :class="!is_collapsed_effective ? 'rotate-180' : 'rotate-0'"
          />
        </button>
      </div>
    </div>
    <Transition
      :css="false"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div v-if="!is_collapsed_effective" class="overflow-hidden">
        <div :class="props.has_header ? 'pt-6.5' : ''">
          <slot name="body" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from "vue";
import { Icon } from "@/components";

const props = defineProps({
  is_collapsable: { type: Boolean, default: false },
  has_header: { type: Boolean, default: true },
  start_collapsed: { type: Boolean, default: false },
});

const rootEl = ref(null);
const is_collapsed = ref(props.start_collapsed);

const is_collapsed_effective = computed(() => {
  if (!props.is_collapsable) return false;
  return is_collapsed.value;
});

function toggle() {
  if (!props.is_collapsable) return;
  is_collapsed.value = !is_collapsed.value;
}

defineExpose({
  open: () => { is_collapsed.value = false; },
  el: rootEl,
});

function stop(el) {
  if (el._onEnd) el.removeEventListener("transitionend", el._onEnd);
  el._onEnd = null;
  el.style.transition = "";
}

function beforeEnter(el) {
  stop(el);
  el.style.height = "0px";
  el.style.opacity = "0";
}

async function enter(el, done) {
  stop(el);

  el.style.height = "0px";
  el.style.opacity = "0";
  el.offsetHeight;

  await nextTick();

  const h = el.scrollHeight;

  el.style.transition = "height 400ms ease, opacity 300ms ease";
  el.style.height = `${h}px`;
  el.style.opacity = "1";

  const onEnd = (e) => {
    if (e.propertyName !== "height") return;
    stop(el);
    done();
  };
  el._onEnd = onEnd;
  el.addEventListener("transitionend", onEnd);
}

function afterEnter(el) {
  // allow natural growth after opening
  el.style.height = "auto";
  el.style.opacity = "";
  el.style.transition = "";
}

function beforeLeave(el) {
  stop(el);
  el.style.height = `${el.scrollHeight}px`;
  el.style.opacity = "1";
  el.offsetHeight;
}

function leave(el, done) {
  stop(el);

  el.style.transition = "height 400ms ease, opacity 300ms ease";
  el.style.height = "0px";
  el.style.opacity = "0";

  const onEnd = (e) => {
    if (e.propertyName !== "height") return;
    stop(el);
    done();
  };
  el._onEnd = onEnd;
  el.addEventListener("transitionend", onEnd);
}

function afterLeave(el) {
  el.style.height = "";
  el.style.opacity = "";
  el.style.transition = "";
}
</script>
